import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { SessionContext } from '../../../../js/app/contexts/SessionContext';
import { useDocumentTitle } from '../../../../js/app/hooks/use-document-title';
import { MAIN_CONTENT_AREA } from '../../utils/layoutStyles';
import { hasPermission, STAFF_MENU_PERMISSIONS } from '../../utils/permissions';

type CardItem = {
  name: string;
  description: string;
  icon: string;
  to: string;
  /** The list__ code that opens this menu; the card hides without it. */
  permission: string;
};

type Category = {
  label: string;
  items: CardItem[];
};

const CATEGORIES: Category[] = [
  {
    label: 'Staffing',
    items: [
      {
        name: 'Needs',
        description: 'Keep track of where staff are needed and how those needs are being filled.',
        icon: 'far fa-users-medical',
        to: '/staff/needs',
        permission: STAFF_MENU_PERMISSIONS.needs,
      },
      {
        name: 'Opportunities',
        description: 'Client staffing needs and the providers being matched to them.',
        icon: 'fal fa-suitcase-medical',
        to: '/staff/opportunities',
        permission: STAFF_MENU_PERMISSIONS.opportunities,
      },
    ],
  },
  {
    label: 'Engagements',
    items: [
      {
        name: 'Providers',
        description:
          'Every clinician on the platform — profile, onboarding progress, rates, and status.',
        icon: 'fal fa-stethoscope',
        to: '/staff/providers',
        permission: STAFF_MENU_PERMISSIONS.providers,
      },
      {
        name: 'Clearances',
        description: 'Credentialing items moving providers toward cleared status.',
        icon: 'fal fa-check-double',
        to: '/staff/clearance',
        permission: STAFF_MENU_PERMISSIONS.clearances,
      },
      {
        name: 'Connections',
        description: 'Provider–facility relationships, rates, and confirmation letters.',
        icon: 'fal fa-handshake',
        to: '/staff/connections',
        permission: STAFF_MENU_PERMISSIONS.connections,
      },
    ],
  },
  {
    label: 'Scheduling & Pay',
    items: [
      {
        name: 'Assignments',
        description: 'Scheduled assignments and shifts across every organization.',
        icon: 'fal fa-calendar-lines-pen',
        to: '/staff/assignments',
        permission: STAFF_MENU_PERMISSIONS.assignments,
      },
      {
        name: 'Timecards',
        description: 'Worked-time review, approval, and payroll handoff.',
        icon: 'fal fa-clock',
        to: '/staff/timecards',
        permission: STAFF_MENU_PERMISSIONS.timecards,
      },
    ],
  },
  {
    label: 'Org Admin',
    items: [
      {
        name: 'Facilities',
        description: 'Client organizations, facility profiles, and contacts.',
        icon: 'fal fa-hospital',
        to: '/staff/facilities',
        permission: STAFF_MENU_PERMISSIONS.facilities,
      },
      {
        name: 'Users',
        description: 'Organization reps, roles, permissions, and invitations.',
        icon: 'far fa-id-badge',
        to: '/staff/users',
        permission: STAFF_MENU_PERMISSIONS.users,
      },
      {
        name: 'Imports',
        description: 'Customer report files — uploads, processing results, and error resolution.',
        icon: 'far fa-file-import',
        to: '/staff/ingest',
        permission: STAFF_MENU_PERMISSIONS.imports,
      },
    ],
  },
];

// Django-served pages outside the SPA — plain anchors, full page loads.
const OTHER_LINKS: Array<{
  name: string;
  description: string;
  icon: string;
  href: string;
  permission?: string;
  adminOnly?: boolean;
}> = [
  {
    name: 'BizIntel',
    description: 'Operational dashboards and reporting.',
    icon: 'fal fa-chart-column',
    href: '/staff-portal',
    permission: STAFF_MENU_PERMISSIONS.bizintel,
  },
  {
    name: 'Admin',
    description: 'Direct data administration.',
    icon: 'fal fa-user-gear',
    href: '/admin',
    // Django admin login rides on the admin flag, not the portal groups.
    adminOnly: true,
  },
];

const CARD_CLASSES =
  'tw:group tw:flex tw:items-start tw:gap-3 tw:rounded-xl tw:border tw:border-zinc-200 tw:bg-white tw:p-4 tw:!no-underline tw:transition tw:hover:border-brand-green-1 tw:hover:shadow-sm tw:focus-visible:outline tw:focus-visible:outline-2 tw:focus-visible:outline-brand-green-1';

const CardBody: React.FC<{ item: { name: string; description: string; icon: string } }> = ({
  item,
}) => (
  <>
    <span className="tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg tw:bg-brand-green-2 tw:text-brand-green-0">
      <i className={`${item.icon} fa-fw tw:text-lg`} aria-hidden="true" />
    </span>
    <span className="tw:min-w-0 tw:flex-1">
      <span className="tw:flex tw:items-center tw:gap-1 tw:font-semibold tw:text-zinc-900">
        {item.name}
        <i
          className="far fa-arrow-right tw:text-xs tw:text-brand-green-1 tw:opacity-0 tw:transition tw:group-hover:translate-x-0.5 tw:group-hover:opacity-100"
          aria-hidden="true"
        />
      </span>
      <span className="tw:mt-0.5 tw:block tw:text-sm tw:text-zinc-500">{item.description}</span>
    </span>
  </>
);

export function StaffHome() {
  useDocumentTitle('Staff Home');
  const { user } = useContext(SessionContext);
  const categories = CATEGORIES.map((category) => ({
    ...category,
    items: category.items.filter((item) => hasPermission(user, item.permission)),
  })).filter((category) => category.items.length > 0);
  const otherLinks = OTHER_LINKS.filter((item) =>
    item.adminOnly
      ? Boolean(user?.is_staff)
      : !item.permission || hasPermission(user, item.permission)
  );

  return (
    <main className={MAIN_CONTENT_AREA} data-testid="staff-home-page">
      <div className="tw:w-full">
        <div className="tw:flex tw:flex-col tw:gap-7">
          {categories.map((category) => (
            <section key={category.label} aria-label={category.label}>
              <h2 className="tw:!mb-2 tw:!text-xs tw:!font-semibold tw:!leading-5 tw:uppercase tw:tracking-wider tw:!text-zinc-500">
                {category.label}
              </h2>
              <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2 tw:xl:grid-cols-3">
                {category.items.map((item) => (
                  <Link
                    key={item.name}
                    to={item.to}
                    className={CARD_CLASSES}
                    data-testid={`staff-home-card-${item.name.toLowerCase()}`}
                  >
                    <CardBody item={item} />
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>

        <section aria-label="Other links" className="tw:mt-8">
          <h2 className="tw:!mb-2 tw:!text-xs tw:!font-semibold tw:!leading-5 tw:uppercase tw:tracking-wider tw:!text-zinc-500">
            Other Links
          </h2>
          <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2 tw:xl:grid-cols-3">
            {otherLinks.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className={CARD_CLASSES}
                data-testid={`staff-home-card-${item.name.toLowerCase()}`}
              >
                <CardBody item={item} />
              </a>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}

export default StaffHome;
